/**
 * Helper (pure) ของปุ่ม "Download Script": เนื้อไฟล์ .txt = ข้อความที่ TTS อ่านออกเสียงจริงของ scene ที่เปิดใช้
 * ต่อกันตามลำดับบนจอ ⇒ ตรงกับวิดีโอที่จะได้ ณ ตอนนี้
 *
 * ข้อมูลใหม่อ่าน `voice_script` โดยตรง ส่วน Run เก่าที่ยังมีเฉพาะ `script` จะผ่าน legacy parser
 * ซึ่งรองรับ notes ทั้งก่อนและหลังบทพูด รวมถึงหลายบล็อกในข้อความเดียว
 *
 * ชื่อไฟล์/การเซฟอยู่ใน episodeDownload.ts + startEpisodeDownload ของการ์ด (ใช้ runner เดียวกับ audio/video zip)
 */
import { ScriptWithImageItem } from "../types/MeGenTypes";
import { parseLegacyVoicePrompt } from "./voicePrompt";

export const SCRIPT_TEXT_MIME = "text/plain;charset=utf-8"; // UTF-8 ไม่ใส่ BOM (convention ของ repo)

/** scene เท่าที่ helper ต้องใช้ — ไม่ต้องส่งฉากเต็ม ๆ เข้ามา */
type SceneScript = Pick<ScriptWithImageItem, "script" | "voice_script" | "active">;

/**
 * ตัดทุกบล็อก DIRECTOR_NOTES: บรรทัด marker เริ่มบล็อก แล้วทิ้งบรรทัด `Key:` ที่ตามติดกันไปเรื่อย ๆ
 * บรรทัดแรกที่ไม่ใช่ `Key:` (รวมบรรทัดว่าง เช่นของ story2scene) = จบบล็อก และเก็บบรรทัดนั้นไว้
 *
 * ไม่มี marker เลย → คืนเนื้อเดิม เพื่อไม่ให้เนื้อที่บังเอิญขึ้นต้นด้วย "Tone:" ถูกตัดทิ้ง
 * ตัดแล้วไม่เหลืออะไร → "" (ผู้เรียกเป็นคนข้าม scene นั้นเอง) · CRLF→LF · บีบบรรทัดว่างติดกันเหลือ 1 · trim
 *
 * ข้อจำกัดที่ยอมรับ (แลกกับกฎ `Key:` แบบทั่วไปที่รองรับ key ใหม่ในอนาคตโดยไม่ต้องมี allowlist):
 * ถ้าบล็อกแบบเก่าตามด้วยบรรทัดภาษาอังกฤษรูป `Word:` ทันที (เช่น "Note: …", "Host: …") บรรทัดนั้นจะถูกนับเป็น
 * คำสั่งเสียงและหายไปด้วย — เนื้อภาษาไทยไม่โดน (regex เป็น ASCII) · รูปแบบปัจจุบันไม่โดนเพราะเนื้ออยู่ก่อนบล็อก
 * (หลังบล็อกมีแต่บรรทัดว่าง) · story2scene ไม่โดนเพราะมีบรรทัดว่างคั่นก่อนเนื้อ
 */
export const stripDirectorNotes = (text: string): string => {
  return parseLegacyVoicePrompt(text).voice_script;
};

/** ข้อความพูดของ scene ที่เปิดใช้ (active !== false) ตามลำดับบนจอ — scene ที่เหลือแต่ notes ถูกข้าม
 *  ใช้ทั้งตอนสร้างไฟล์และตอนตัดสินว่าปุ่มกดได้ (จำนวนที่ได้ = เกณฑ์เปิดปุ่ม) */
export const spokenScripts = (scenes: ReadonlyArray<SceneScript> | null | undefined): string[] =>
  (scenes ?? [])
    .filter((s) => s.active !== false)
    .map((s) => s.voice_script ?? stripDirectorNotes(s.script ?? ""))
    .filter((t) => t !== "");

/** เนื้อไฟล์: ข้อความพูดของแต่ละ scene คั่นด้วยบรรทัดว่าง 1 บรรทัด ลงท้าย \n เดียว
 *  ไม่มี header ใด ๆ (ไฟล์ต้องเป็นข้อความล้วนที่เอาไปใช้ต่อได้ทันที) · ไม่มี scene ที่พูด → "" */
export const buildScriptTextFile = (
  scenes: ReadonlyArray<SceneScript> | null | undefined
): { content: string; sceneCount: number } => {
  const parts = spokenScripts(scenes);
  return { content: parts.length ? `${parts.join("\n\n")}\n` : "", sceneCount: parts.length };
};
